import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';

export interface ITextureSwatchProps<T = any> {
  id: string;
  texture: T;
  selected: boolean;
  showLabel: boolean;
  getColorOrImage: (texture: T) => string;
  getLabel: (texture: T) => string;
  onSelected?: (texture: T) => void | undefined;
  tabIndex?: number | undefined;
}

const getStyleClasses = (theme: ITheme, selectable: boolean) => {
  const root: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.1em',
  };
  
  const swatchBorder: IStyle = {
    boxSizing: 'border-box',
    width: '1em',
    height: '1em',
    borderRadius: '0.5em',
    border: `0.06em solid ${theme.palette.neutralSecondary}`,
    backgroundColor: theme.palette.white,
    position: 'relative',
  };
  
  const swatchBorderDefault: IStyle = {
    ...swatchBorder,
    borderColor: theme.palette.neutralSecondary,
  };
  if(selectable) {
    swatchBorderDefault['&:hover'] = {
      borderColor: theme.palette.neutralPrimary,
    };
    swatchBorderDefault['&:hover > div'] = {
      opacity: 0.9,
    };
  }
  
  const swatchBorderSelected: IStyle = {
    ...swatchBorder,
    borderColor: theme.palette.themeSecondary,
  };
  if(selectable) {
    swatchBorderSelected['&:hover'] = {
      borderColor: theme.palette.themePrimary,
    };
    swatchBorderSelected['&:hover > div'] = {
      opacity: 0.9,
    };
  }

  const swatch: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '0.8em',
    height: '0.8em',
    borderRadius: '0.396em',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    position: 'absolute',
    left: '0.04em',
    top: '0.04em',
  };
  
  const label: IStyle = {
    display: 'block',
    fontSize: '0.18em',
    maxWidth: '5em', // because font-size scales it down
    textAlign: 'center',
    fontWeight: 600,
    lineHeight: '1.4em',
  };

  return mergeStyleSets({
    root,
    swatchBorderDefault,
    swatchBorderSelected,
    swatch,
    label,
  });
}

export const UiTextureSwatch: React.FunctionComponent<ITextureSwatchProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme, !!props.onSelected);
  const { texture, getColorOrImage } = props;
  const label = props.getLabel(texture);
  const swatchStyle = useMemo<React.CSSProperties>(() => {
    const media = getColorOrImage(texture);
    if(media[0] === '#') {
      return { backgroundColor: media };
    }
    return { backgroundImage: `url("${media}")` };
  }, [texture, getColorOrImage]);

  const onSwatchClicked = () => {
    !!props.onSelected && props.onSelected(texture);
  }

  return (
    <div id={props.id} className={cs.root}>
      <div
        className={props.selected ? cs.swatchBorderSelected : cs.swatchBorderDefault}
        role={!!props.onSelected ? 'checkbox' : undefined}
        tabIndex={props.tabIndex}
        aria-checked={!!props.onSelected ? props.selected : undefined}
        onClick={onSwatchClicked}
      >
        <div
          className={cs.swatch}
          style={swatchStyle}
          aria-label={props.showLabel === false ? label : undefined}
          title={props.showLabel === false ? label : undefined}
        />
      </div>
      {props.showLabel &&
      <label
        htmlFor={props.id}
        className={cs.label}
      >
        {label}
      </label>}
    </div>
  );
};
